<script setup lang="ts">
/**
 * 스튜디오 Stage 이동(공통) — Previous / Next + **빈 칸 확인**.
 *   · 다음 Stage 로 갈 때(Next 버튼, 또는 위 스테퍼 클릭 → requestGo) 지금 Stage 에 빈 칸(`.lv-field.empty`)이 있으면
 *     "빈 칸 N개가 있습니다. 비워 둔 채 넘어가시겠습니까?" 를 묻는다.
 *       - Yes → 그대로 이동(빈 칸은 빈칸으로 둔다)
 *       - No  → 이동하지 않고 빈 칸을 빨간색으로 표시 + 첫 빈 칸으로 스크롤, [다음 빈 칸] 안내
 *   · 이전 Stage 로 돌아갈 때는 묻지 않는다.
 *   · 빨간 표시는 칸 요소의 data-missing 속성 + CSS `.lv-field.empty[data-missing]`
 *     → 값을 채우면 empty 클래스가 빠지면서 자동으로 사라진다(Vue 가 class 를 다시 쓰므로 class 대신 속성 사용).
 *   · isOptional(key) 로 확인에서 뺄 칸을 줄 수 있다(칸 요소의 data-key = 필드 키). 기본은 모든 칸.
 *   사용: <StudioStageNav ref="stageNav" :current="currentStage" :total="totalStages"
 *          :get-stage-el="() => stageEls[currentStage]" @go="go" />
 *        스테퍼 클릭은 @click="stageNav?.requestGo(st.no)"
 */
import { ref, watch } from 'vue';

const props = withDefaults(defineProps<{
  current: number;
  total: number;
  /** 지금 Stage 의 루트 요소 */
  getStageEl: () => HTMLElement | null | undefined;
  /** 확인에서 뺄 칸(필드 키 기준) — 기본 없음 */
  isOptional?: (key: string) => boolean;
  /** 빈 칸 selector — CddVarField 계열 공통 */
  emptySelector?: string;
}>(), {
  isOptional: () => false,
  emptySelector: '.lv-field.empty',
});
const emit = defineEmits<{ (e: 'go', n: number): void }>();

const missing = ref<HTMLElement[]>([]);
const cursor = ref(0);
const confirmOpen = ref(false);
const confirmCount = ref(0);
let pendingTarget = 0;

function emptyFields(): HTMLElement[] {
  const root = props.getStageEl();
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(props.emptySelector)).filter((el) => {
    if (el.offsetParent === null) return false; // 숨은 칸(접힌 조건부 등)
    const key = el.getAttribute('data-key') || '';
    return !(key && props.isOptional(key));
  });
}
function focusAt(i: number) {
  const el = missing.value[i];
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const editable = el.querySelector<HTMLElement>('[contenteditable="true"], input');
  setTimeout(() => editable?.focus({ preventScroll: true }), 250);
}
function clearMarks() {
  const root = props.getStageEl();
  root?.querySelectorAll('[data-missing]').forEach((el) => el.removeAttribute('data-missing'));
}

/** Stage 이동 요청 — 앞으로 갈 때만 빈 칸 확인 */
function requestGo(n: number) {
  if (n < 1 || n > props.total || n === props.current) return;
  if (n < props.current) { missing.value = []; emit('go', n); return; }
  const list = emptyFields();
  if (!list.length) { missing.value = []; emit('go', n); return; }
  pendingTarget = n;
  confirmCount.value = list.length;
  confirmOpen.value = true;
}
/** Yes — 빈 칸은 비워 둔 채 이동 */
function confirmYes() {
  confirmOpen.value = false;
  missing.value = [];
  emit('go', pendingTarget);
}
/** No — 이동하지 않고 빈 칸 표시 */
function confirmNo() {
  confirmOpen.value = false;
  clearMarks();
  const list = emptyFields();
  list.forEach((el) => el.setAttribute('data-missing', '1'));
  missing.value = list;
  cursor.value = 0;
  focusAt(0);
}
function nextMissing() {
  // 그새 채운 칸은 빼고 다시 센다
  missing.value = missing.value.filter((el) => el.isConnected && el.matches(props.emptySelector));
  if (!missing.value.length) return;
  cursor.value = (cursor.value + 1) % missing.value.length;
  focusAt(cursor.value);
}
// Stage 가 바뀌면 안내 닫기
watch(() => props.current, () => { missing.value = []; });

defineExpose({ requestGo });
</script>

<template>
  <div class="ssn">
    <div v-if="missing.length" class="ssn-warn" role="alert">
      <span class="ssn-warn-t">⚠ {{ missing.length }} empty field{{ missing.length > 1 ? 's are' : ' is' }} marked in red in this stage.</span>
      <button type="button" class="ssn-link" @click="nextMissing">Go to next empty field</button>
    </div>
    <div class="ssn-row">
      <button v-if="current > 1" type="button" class="ssn-btn" @click="requestGo(current - 1)">&larr; Previous</button>
      <span class="ssn-count">Stage {{ current }} / {{ total }}</span>
      <button v-if="current < total" type="button" class="ssn-btn primary" @click="requestGo(current + 1)">Next &rarr;</button>
      <button v-else type="button" class="ssn-btn primary" disabled>All stages complete &#10003;</button>
    </div>

    <v-dialog v-model="confirmOpen" max-width="420" persistent>
      <div class="ssn-dlg">
        <div class="ssn-dlg-ic">!</div>
        <div class="ssn-dlg-t">There {{ confirmCount > 1 ? 'are' : 'is' }} {{ confirmCount }} empty field{{ confirmCount > 1 ? 's' : '' }} in this stage.</div>
        <div class="ssn-dlg-s">Do you want to continue and leave {{ confirmCount > 1 ? 'them' : 'it' }} blank?</div>
        <div class="ssn-dlg-a">
          <button type="button" class="ssn-btn" @click="confirmNo">No, show me</button>
          <button type="button" class="ssn-btn primary" @click="confirmYes">Yes, continue</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<style scoped>
.ssn { width: 794px; max-width: 100%; margin: 36px auto 12px; }
.ssn-row { display: flex; align-items: center; justify-content: center; gap: 16px; }
.ssn-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px 20px; cursor: pointer; }
.ssn-btn:hover:not(:disabled) { background: #f2f6fc; }
.ssn-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.ssn-btn.primary:hover:not(:disabled) { background: #1550b8; }
.ssn-btn:disabled { opacity: 0.55; cursor: default; }
.ssn-count { font-size: 12.5px; font-weight: 800; color: #8a97b5; }
.ssn-warn { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; margin: 0 0 14px; padding: 10px 14px; border-radius: 10px; background: #fdecec; border: 1px solid #f5c2c0; }
.ssn-warn-t { font-size: 12.5px; font-weight: 700; color: #b3261e; }
.ssn-link { border: 1px solid #f0a8a4; background: #fff; color: #b3261e; font-size: 12px; font-weight: 700; border-radius: 999px; padding: 5px 12px; cursor: pointer; }
.ssn-dlg { background: #fff; border-radius: 16px; padding: 24px 24px 20px; text-align: center; }
.ssn-dlg-ic { width: 40px; height: 40px; margin: 0 auto 12px; border-radius: 50%; background: #fff4e5; color: #d97706; font-size: 20px; font-weight: 900; display: flex; align-items: center; justify-content: center; }
.ssn-dlg-t { font-size: 15px; font-weight: 800; color: #1f2d4d; }
.ssn-dlg-s { font-size: 13px; color: #5b6b82; margin-top: 6px; }
.ssn-dlg-a { display: flex; justify-content: center; gap: 10px; margin-top: 20px; }
</style>

<style>
/* 빈 칸 표시 — 문서 칸(CddVarField 계열)에 붙는다. 값을 채우면 .empty 가 빠지며 자동으로 사라짐 */
.lv-field.empty[data-missing] {
  background: #fdecec !important;
  box-shadow: inset 0 -2px 0 #e53935 !important;
  outline: 1px solid #f5a8a4;
  border-radius: 3px;
}
.lv-field.empty[data-missing] .lv-text:empty::before,
.lv-field.empty[data-missing] .lv-text::before { color: #c62828 !important; }
</style>
